import type { AdminFlowCardsProps } from "../../models/admin-flow-cards.models";
import type { AdminFlowNode } from "../../models/admin-flows.models";
import {
  ADMIN_FLOW_WIZARD_ACTION_CLASS,
  ADMIN_FLOW_WIZARD_FIELD_CLASS,
  ADMIN_FLOW_WIZARD_STEPS,
} from "../../utils/admin-flow-cards.constants";
import { ADMIN_FLOW_SOURCE_OPTIONS } from "../../utils/admin-flow-whiteboard.constants";
import { getAdminFlowSourceArguments } from "../../utils/admin-flow-whiteboard.utils";
import {
  connectAdminFlowNodes,
  createAdminFlowNode,
  orderAdminFlowNodes,
  validateAdminFlow,
} from "../../utils/admin-flows.utils";
import AdminFlowWizardCard from "./AdminFlowWizardCard";
import { useState } from "react";

// Present the shared flow as a guided series of editable cards.
// Cards retain graph dependencies and canvas coordinates so switching views never flattens a branched flow.
const AdminFlowCards = ({ flow, tools, onChange }: AdminFlowCardsProps) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [addError, setAddError] = useState("");
  const step = ADMIN_FLOW_WIZARD_STEPS[stepIndex]!;
  const orderedNodes = orderAdminFlowNodes(flow);
  const visibleNodes = orderedNodes.filter((node) =>
    step.id === "sources"
      ? node.kind === "source"
      : step.id === "agents"
        ? node.kind === "agent" || node.kind === "merge"
        : node.kind === "text" || node.kind === "email",
  );
  const errors = validateAdminFlow(flow);

  const addNode = (kind: AdminFlowNode["kind"], source = "") => {
    setAddError("");
    const node = createAdminFlowNode(kind, flow.nodes.length);
    const sourceLabel = ADMIN_FLOW_SOURCE_OPTIONS.find((option) => option.value === source)?.label;
    let nextFlow = {
      ...flow,
      nodes: [
        ...flow.nodes,
        {
          ...node,
          source: source || node.source,
          label: sourceLabel || node.label,
          arguments: kind === "source" ? getAdminFlowSourceArguments(source || node.source) : node.arguments,
        },
      ],
    };

    // Start new steps after the current terminal processing blocks.
    // Adding a source remains independent until an administrator chooses its downstream inputs.
    if (kind !== "source") {
      const terminalNodes = flow.nodes.filter(
        (entry) =>
          entry.kind !== "text" &&
          entry.kind !== "email" &&
          !flow.edges.some((edge) => {
            const downstream = flow.nodes.find((candidate) => candidate.id === edge.to);

            return edge.from === entry.id && downstream?.kind !== "text" && downstream?.kind !== "email";
          }),
      );

      try {
        terminalNodes.forEach((entry) => {
          nextFlow = connectAdminFlowNodes(nextFlow, entry.id, node.id);
        });
      } catch (error) {
        setAddError(error instanceof Error ? error.message : "This block could not be connected.");

        return;
      }
    }

    onChange(nextFlow);
  };

  const changeStep = (index: number) => {
    setStepIndex(index);
    setAddError("");
  };

  return (
    <section data-test="admin-flow-cards" className="grid min-h-0 gap-4 lg:grid-cols-[210px_minmax(0,1fr)]">
      <nav
        aria-label="Flow setup steps"
        className="self-start rounded border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
      >
        <h2 className="px-2 pb-3 text-sm font-semibold">Your setup</h2>
        <ol className="space-y-1">
          {ADMIN_FLOW_WIZARD_STEPS.map((entry, index) => (
            <li key={entry.id}>
              <button
                type="button"
                data-test={`admin-flow-wizard-step-${entry.id}`}
                aria-current={stepIndex === index ? "step" : undefined}
                onClick={() => changeStep(index)}
                className={`flex w-full items-start gap-3 rounded px-2 py-3 text-left text-xs transition-colors ${stepIndex === index ? "bg-[#E9F4F3] text-green dark:bg-green/20 dark:text-emerald-300" : "hover:bg-gray-100 dark:hover:bg-gray-700"}`}
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#E9F4F3] text-green dark:bg-gray-700 dark:text-emerald-300">
                  {index + 1}
                </span>
                <span className="pt-1 font-semibold">{entry.title}</span>
              </button>
            </li>
          ))}
        </ol>
      </nav>
      <div className="min-w-0 space-y-4">
        <header className="rounded border border-gray-200 bg-white px-5 py-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-base font-semibold">{step.title}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{step.description}</p>
        </header>
        {step.id === "trigger" && (
          <div
            data-test="admin-flow-wizard-trigger"
            className="space-y-4 rounded border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800"
          >
            <label className="block text-xs font-semibold">
              Flow name
              <input
                data-test="admin-flow-wizard-name"
                value={flow.name}
                onChange={(event) => onChange({ ...flow, name: event.target.value })}
                maxLength={120}
                className={`mt-2 ${ADMIN_FLOW_WIZARD_FIELD_CLASS}`}
              />
            </label>
            <div className="rounded bg-[#E9F4F3] p-3 text-sm dark:bg-green/10">
              Manual trigger is always available. Use Test flow to review a run with the current settings.
            </div>
            <label className="block text-xs font-semibold">
              Planned schedule
              <select
                data-test="admin-flow-wizard-trigger-select"
                value={flow.schedule.cron}
                onChange={(event) => onChange({ ...flow, schedule: { ...flow.schedule, cron: event.target.value } })}
                className={`mt-2 ${ADMIN_FLOW_WIZARD_FIELD_CLASS}`}
              >
                <option value="0 7 * * *">Every day at 07:00</option>
                <option value="0 7 * * 1-5">Weekdays at 07:00</option>
                <option value="0 15 * * 5">Friday at 15:00</option>
                {!["0 7 * * *", "0 7 * * 1-5", "0 15 * * 5"].includes(flow.schedule.cron) && (
                  <option value={flow.schedule.cron}>{flow.schedule.cron || "Choose a schedule"}</option>
                )}
              </select>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Timezone: {flow.schedule.timezone}. Preparing a schedule here does not enable it. Open Schedule to review
              its execution settings.
            </p>
          </div>
        )}
        {step.id !== "trigger" && step.id !== "review" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" data-test={`admin-flow-wizard-add-${step.id}`}>
              {step.id === "sources" && (
                <>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-vessels"
                    onClick={() => addNode("source", "vessels")}
                  >
                    + Vessels
                  </button>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-documents"
                    onClick={() => addNode("source", "documents")}
                  >
                    + Documents
                  </button>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-support"
                    onClick={() => addNode("source", "support")}
                  >
                    + Support inbox
                  </button>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-offline-tech-form"
                    onClick={() => addNode("source", "offline_tech_form")}
                  >
                    + Offline Tech Form inbox
                  </button>
                </>
              )}
              {step.id === "agents" && (
                <>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-agent"
                    onClick={() => addNode("agent")}
                  >
                    + AI step
                  </button>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-merge"
                    onClick={() => addNode("merge")}
                  >
                    + Merge inputs
                  </button>
                </>
              )}
              {step.id === "outputs" && (
                <>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-text"
                    onClick={() => addNode("text")}
                  >
                    + Text output
                  </button>
                  <button
                    type="button"
                    className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
                    data-test="admin-flow-wizard-add-email"
                    onClick={() => addNode("email")}
                  >
                    + Email for review
                  </button>
                </>
              )}
            </div>
            {addError && (
              <p
                role="alert"
                data-test="admin-flow-wizard-add-error"
                className="text-xs text-red-700 dark:text-red-300"
              >
                {addError}
              </p>
            )}
            {visibleNodes.length === 0 && (
              <p
                data-test="admin-flow-wizard-empty"
                className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400"
              >
                No {step.id === "agents" ? "AI steps" : step.id} added yet. Add a card to get started.
              </p>
            )}
            {visibleNodes.map((node, index) => (
              <AdminFlowWizardCard
                key={node.id}
                flow={flow}
                node={node}
                tools={tools}
                position={index}
                onChange={onChange}
              />
            ))}
          </div>
        )}
        {step.id === "review" && (
          <div
            data-test="admin-flow-wizard-review"
            className="space-y-4 rounded border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800"
          >
            <h3 className="font-semibold">{flow.name || "Untitled flow"}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {flow.nodes.length} blocks · {flow.edges.length} connections · {flow.schedule.timezone}
            </p>
            {errors.length > 0 ? (
              <div
                role="status"
                className="rounded bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
              >
                <p className="font-semibold">Complete these settings before publishing</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {errors.map((error, index) => (
                    <li key={`${index}-${error}`}>{error}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="rounded bg-[#E9F4F3] p-3 text-sm dark:bg-green/10">
                The flow is ready for review. Use Publish in the page header to save a runnable version.
              </p>
            )}
            <ol className="space-y-2">
              {orderedNodes.map((node, index) => {
                const inputLabels = flow.edges
                  .filter((edge) => edge.to === node.id)
                  .map((edge) => flow.nodes.find((entry) => entry.id === edge.from)?.label || "Missing block");

                return (
                  <li key={node.id} className="rounded border border-gray-200 px-3 py-2 dark:border-gray-700">
                    <span className="text-sm font-semibold">
                      {index + 1}. {node.label || "Untitled block"}
                    </span>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {inputLabels.length > 0 ? `Inputs: ${inputLabels.join(", ")}` : "Starting point"}
                      {node.toolName ? ` · ${node.toolName}` : ""}
                    </p>
                  </li>
                );
              })}
            </ol>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Email requires separate review and permission. Publishing never sends an email or enables a schedule.
            </p>
          </div>
        )}
        <footer className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            type="button"
            data-test="admin-flow-wizard-back"
            disabled={stepIndex === 0}
            onClick={() => changeStep(stepIndex - 1)}
            className={ADMIN_FLOW_WIZARD_ACTION_CLASS}
          >
            Back
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Step {stepIndex + 1} of {ADMIN_FLOW_WIZARD_STEPS.length}
          </span>
          <button
            type="button"
            data-test="admin-flow-wizard-continue"
            disabled={stepIndex === ADMIN_FLOW_WIZARD_STEPS.length - 1}
            onClick={() => changeStep(stepIndex + 1)}
            className="min-h-9 rounded bg-green px-4 py-2 text-xs font-semibold text-white hover:bg-[#166b60] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue
          </button>
        </footer>
      </div>
    </section>
  );
};

export default AdminFlowCards;
